import React, { useRef, useState } from 'react'
import {
  Button,
  Dropdown,
  Input,
  Notification,
  Content,
  NotificationType,
  Textarea
} from '@ampcore/adel-react'
import { Spacer } from '@ampcore/design-system'
import StatesData from '../views/usa-states.json'
import { isValidEmail, isValidZipcode } from '../../../common/utils'
import { formSelectedData, CONSTs } from '../views/utils'
import '../views/index.css'
import xss from 'xss'

export type SSFieldName =
  | 'firstname'
  | 'lastname'
  | 'emailaddress'
  | 'street'
  | 'city'
  | 'state'
  | 'zipcode'
  | 'inquiry'

export type SSFormData = {
  firstname: string | null
  lastname: string | null
  emailaddress: string | null
  street: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  inquiry: string | null
}

export type SSErrorFields = {
  firstname: boolean
  lastname: boolean
  emailaddress: boolean
  street: boolean
  city: boolean
  state: boolean
  zipcode: boolean
  inquiry: boolean
  emailWrongFormat: boolean
}

export type SSFormLabels = {
  firstname: string
  lastname: string
  emailaddress: string
  street?: string
  city?: string
  state?: string
  zipcode?: string
  inquiry?: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type SSFormHeading = {
  title?: string
}

export type SSFormEmailInfo = {
  emailApi: string
  subject: string
  toEmailAddress: string
}

export type SSFormErrorMessages = {
  errorFirstname: string
  errorLastname: string
  errorEmailaddress: string
  errorEmailaddressWrongFormat: string
  errorZipcode: string
}

export type SSServiceMessages = {
  serviceSuccess: string
  serviceSuccessThankyou: string
  serviceFailed: string
}

export type SSFormPleaseNote = {
  message: string
}

export type SSShowServerResponse = (props: unknown, message?: string) => void

export type ContactFormSecureSiteContent = {
  componentSpacing?: React.ComponentProps<typeof Spacer>['spacing']
  formLabels: SSFormLabels
  formHeading?: SSFormHeading
  formEmailInfo?: SSFormEmailInfo
  formErrorMessages: SSFormErrorMessages
  serviceMessages?: Partial<SSServiceMessages>
  formPleaseNote?: SSFormPleaseNote
  formTemplate?: string
}

export type ContactFormSecureSiteCallback = (
  items: unknown,
  emailInfo: SSFormEmailInfo | undefined,
  showServerResponse: SSShowServerResponse
) => unknown

export type ContactFormSecureSiteProps = {
  callback?: ContactFormSecureSiteCallback
  name?: string
  content: ContactFormSecureSiteContent
  testId?: string
}

const ContactFormSecureSite = (props: ContactFormSecureSiteProps) => {
  const { callback, name, content, testId } = props

  const [formData, setFormData] = useState<SSFormData>({
    firstname: null,
    lastname: null,
    emailaddress: null,
    street: null,
    city: null,
    state: null,
    zipcode: null,
    inquiry: null
  })

  const [errorFields, setErrorFields] = useState<SSErrorFields>({
    firstname: false,
    lastname: false,
    emailaddress: false,
    street: false,
    city: false,
    state: false,
    zipcode: false,
    inquiry: false,
    emailWrongFormat: false
  })

  const [loading, setLoading] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)

  const stateRef = useRef<HTMLSelectElement | null>(null)

  const showServerResponse: SSShowServerResponse = (props, message) => {
    if (props && message) {
      if (message === CONSTs.SUCCESS) {
        setShowSuccessMessage(true)
        setShowServiceFailedMessage(false)
      } else {
        setShowSuccessMessage(false)
        setShowServiceFailedMessage(true)
      }
      setLoading(false)
    }
  }

  const validateField = (fieldName: SSFieldName, value: string) => {
    const fieldErrors: SSErrorFields = { ...errorFields }
    const updatedFormData: SSFormData = { ...formData }
    const isNotEmpty = value.trim().length > 0

    switch (fieldName) {
    case 'firstname': {
      fieldErrors.firstname = !isNotEmpty
      updatedFormData.firstname = value.trim() || null
      break
    }
    case 'lastname': {
      fieldErrors.lastname = !isNotEmpty
      updatedFormData.lastname = value.trim() || null
      break
    }
    case 'emailaddress': {
      const isEmailValid = isValidEmail(value)
      fieldErrors.emailaddress = !isEmailValid
      fieldErrors.emailWrongFormat = !isEmailValid && isNotEmpty
      updatedFormData.emailaddress = fieldErrors.emailaddress ? null : value
      break
    }
    case 'zipcode': {
      const isZipcodeValid = isValidZipcode(value)
      fieldErrors.zipcode = !isZipcodeValid
      updatedFormData.zipcode = isZipcodeValid ? value : null
      break
    }
    case 'street':
    case 'city':
    case 'inquiry': {
      updatedFormData[fieldName] = value || null
      break
    }
    default:
      break
    }

    setFormData(updatedFormData)
    setErrorFields(fieldErrors)
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null
    if (!target || !target.name) return
    validateField(target.name as SSFieldName, target.value || '')
  }

  const handleSelectState = (value: string) => {
    setFormData(prevState => ({ ...prevState, state: value || null }))
  }

  const isFormComplete = Object.entries(formData).every(([key, value]) => {
    if (
      key === 'state' ||
      key === 'city' ||
      key === 'street' ||
      key === 'inquiry'
    )
      return true
    return value !== null
  })

  const submitForm = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    const { formEmailInfo, formTemplate } = content

    if (!isFormComplete) {
      const fieldErrors: SSErrorFields = { ...errorFields }
      fieldErrors.firstname = !formData.firstname
      fieldErrors.lastname = !formData.lastname
      fieldErrors.emailaddress = !isValidEmail(formData.emailaddress || '')
      fieldErrors.zipcode = !isValidZipcode(formData.zipcode || '')
      setErrorFields(fieldErrors)
      return
    }

    setLoading(true)
    const items = formSelectedData(formData, formTemplate)
    if (callback && typeof callback === 'function' && items) {
      callback(items, formEmailInfo, showServerResponse)
    }
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: SSFormErrorMessages
  ): string => {
    return wrongEmail
      ? message.errorEmailaddressWrongFormat
      : message.errorEmailaddress
  }

  return (
    <Spacer spacing={content.componentSpacing}>
      {showSuccessMessage && content.serviceMessages?.serviceSuccess && (
        <Notification
          type={NotificationType.CONFIRM}
          dismissable={false}
          className="SecureSiteFormNotification u-sizeConstrained"
          children={
            <>
              <h3 className="u-paddingBottom16">
                {content.serviceMessages?.serviceSuccessThankyou}
              </h3>
              <p
                className="u-paddingTop16 u-paddingBottom16"
                dangerouslySetInnerHTML={{
                  __html: xss(content.serviceMessages.serviceSuccess)
                }}
              ></p>
            </>
          }
        />
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <form
            name={name}
            className="u-marginBottom24 u-size5of12 u-md-size8of12 u-sm-sizeFull"
            onBlur={handleFieldChange}
            onKeyUp={handleFieldChange}
          >
            {content.formHeading?.title && (
              <h2 className="u-marginBottom24">{content.formHeading.title}</h2>
            )}
            <Input
              name="firstname"
              id="firstname"
              placeholder=""
              label={content.formLabels.firstname}
              error={errorFields.firstname}
              message={content.formErrorMessages.errorFirstname}
              testId={testId ? `${testId}-firstname` : undefined}
            />
            <Input
              name="lastname"
              id="lastname"
              placeholder=""
              label={content.formLabels.lastname}
              error={errorFields.lastname}
              message={content.formErrorMessages.errorLastname}
              testId={testId ? `${testId}-lastname` : undefined}
            />
            <Input
              name="emailaddress"
              id="email"
              placeholder=""
              label={content.formLabels.emailaddress}
              error={errorFields.emailaddress}
              message={setEmailErrorMessage(
                errorFields.emailWrongFormat,
                content.formErrorMessages
              )}
              testId={testId ? `${testId}-email` : undefined}
            />
            {content.formLabels.street && (
              <Input
                name="street"
                id="street"
                placeholder=""
                label={content.formLabels.street}
                testId={testId ? `${testId}-street` : undefined}
              />
            )}
            {content.formLabels.city && (
              <Input
                name="city"
                id="city"
                placeholder=""
                label={content.formLabels.city}
                testId={testId ? `${testId}-city` : undefined}
              />
            )}
            {content.formLabels.state && (
              <Dropdown
                name="state"
                id="state"
                placeholder="Select State"
                label={content.formLabels.state}
                reference={stateRef}
                callback={handleSelectState}
                data={StatesData}
                className="u-marginBottom20"
              />
            )}
            {content.formLabels.zipcode && (
              <Input
                name="zipcode"
                id="zipcode"
                placeholder=""
                label={content.formLabels.zipcode}
                error={errorFields.zipcode}
                message={content.formErrorMessages.errorZipcode}
                testId={testId ? `${testId}-zipcode` : undefined}
              />
            )}
            {content.formLabels.inquiry && (
              <Textarea
                name="inquiry"
                id="inquiry"
                placeholder=""
                label={content.formLabels.inquiry}
                row={3}
                testId={testId ? `${testId}-inquiry` : undefined}
              />
            )}
            {showServiceFailedMessage &&
              content.serviceMessages?.serviceFailed && (
              <Notification
                type={NotificationType.ALERT}
                message={content.serviceMessages.serviceFailed}
                dismissable={false}
                className="u-marginBottom24"
              />
            )}
            <Button
              onClick={submitForm}
              isLoading={loading}
              data-analytics={'Button'}
              data-tracking={'Button'}
              children={
                loading
                  ? content.formLabels.buttonLoadingLabel
                  : content.formLabels.buttonlabel
              }
            />
          </form>
          {content.formPleaseNote?.message && (
            <Content className={'u-marginBottom24'}>
              <div
                className="Content"
                dangerouslySetInnerHTML={{
                  __html: xss(content.formPleaseNote.message)
                }}
              />
            </Content>
          )}
        </div>
      )}
    </Spacer>
  )
}

export default ContactFormSecureSite
